import React from "react";
import { useDataProvider, useNotify } from "react-admin";
import { useQueryClient } from "react-query";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { Confirm } from "react-admin";
import {
  DocumentMoveDestination,
  DocumentMoveSectionKey,
  DocumentMoveSourceScope,
  MovableDocument,
  getMovePayloadDocumentId,
  getMovePayloadSourceId,
  isRealDocumentId,
  validateDocumentMove,
} from "./documentMoveValidation";
import { invalidateDocumentQueriesForSections } from "./documentMoveRefresh";
import { buildIdentityCleanupPayload } from "./documentMoveIdCleanup";
import {
  MoveResultNotification,
  buildMoveResultNotificationSequence,
  nextNotificationVisibleUntil,
  remainingNotificationHoldMs,
  toErrorMessage,
} from "./documentMoveNotifications";

type DocumentMoveContextValue = {
  bulkActionsEnabled: boolean;
  selectedDocuments: MovableDocument[];
  activeDocuments: MovableDocument[];
  selectedIds: Set<string>;
  pendingMoveIds: Set<string>;
  lockedDocumentIds: Set<string>;
  isMoveMode: boolean;
  isMoving: boolean;
  isDeleting: boolean;
  processingScopeKey: string | null;
  processingScopeKeys: Set<string>;
  dragSelectedIds: string[];
  lastMessage: string;
  // Scope the message belongs to, or null for a message that isn't tied to a
  // single source. Strips only render messages for their own scope so a
  // warning doesn't appear under every document list on the page at once.
  lastMessageScopeKey: string | null;
  celebrationId: string;
  registerDocuments: (scopeKey: string, documents: MovableDocument[]) => void;
  unregisterDocuments: (scopeKey: string) => void;
  getDocumentsForScope: (scope: DocumentMoveSourceScope) => MovableDocument[];
  toggleDocumentSelection: (document: MovableDocument) => void;
  setDocumentLocked: (documentId: string, locked: boolean) => void;
  selectDocuments: (documents: MovableDocument[]) => void;
  clearSelection: (options?: { closeMoveFallback?: boolean }) => void;
  beginMoveMode: () => void;
  setDragSelectedIds: (ids: string[]) => void;
  moveSelectionToDestination: (destination: DocumentMoveDestination) => Promise<void>;
  requestDeleteSelection: (options?: { fromDrag?: boolean }) => void;
};

const noopAsync = async () => undefined;

export const DocumentMoveContext = React.createContext<DocumentMoveContextValue>({
  bulkActionsEnabled: true,
  selectedDocuments: [],
  activeDocuments: [],
  selectedIds: new Set(),
  pendingMoveIds: new Set(),
  lockedDocumentIds: new Set(),
  isMoveMode: false,
  isMoving: false,
  isDeleting: false,
  processingScopeKey: null,
  processingScopeKeys: new Set(),
  dragSelectedIds: [],
  lastMessage: "",
  lastMessageScopeKey: null,
  celebrationId: "",
  registerDocuments: () => undefined,
  unregisterDocuments: () => undefined,
  getDocumentsForScope: () => [],
  toggleDocumentSelection: () => undefined,
  setDocumentLocked: () => undefined,
  selectDocuments: () => undefined,
  clearSelection: () => undefined,
  beginMoveMode: () => undefined,
  setDragSelectedIds: () => undefined,
  moveSelectionToDestination: noopAsync,
  requestDeleteSelection: () => undefined,
});

// Custom drag-and-drop MIME marker set when a movable document drag starts.
// Drop targets check for it in dragover/drop to recognize a document drag
// synchronously — without waiting for the drag-selection React state to
// propagate (that re-render lag is what made outline targets slow to activate
// and let documents be dropped before a target was truly "armed").
export const DOCUMENT_DRAG_MIME = "application/x-dv-document-move";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const MOVE_REQUEST_DELAY_MS = 3000;
const MOVE_RETRY_DELAYS_MS = [2500, 5000];
const MOVE_SETTLE_REFRESH_DELAY_MS = 8000;
// Destinations that don't run document processing (Other Documents) commit
// almost immediately, so their settle refresh only needs to cover request
// round-trip time, not an OCR pipeline.
const MOVE_SETTLE_REFRESH_FAST_DELAY_MS = 2000;
// A move request is accepted before the backend has written the relocated
// document into every section response. Recheck a few times after the first
// settle window so a slow SOCR write appears without requiring a browser
// refresh. Each check remains limited to the move's source and destination.
const MOVE_SETTLE_REFRESH_RETRY_OFFSETS_MS = [0, 4000, 8000];
const CELEBRATION_DURATION_MS = 1600;

// The 3s inter-request spacing and the long settle window exist because the
// backend runs OCR/metadata processing on documents moved into most sections
// and can fail when a follow-up request arrives mid-processing. Other
// Documents is pure storage — no processing — so moves into it skip both.
const destinationRequiresProcessing = (destination: DocumentMoveDestination) =>
  destination.sectionKey !== "other";

// Only transient failures are worth retrying. A 4xx is a definitive answer
// (validation, permissions, not found) — retrying doubles backend load and
// delays surfacing the real error. When no status is available, assume the
// known transient backend race and retry.
const isRetryableError = (error: any): boolean => {
  const status = Number(error?.status ?? error?.body?.status ?? NaN);
  if (!Number.isFinite(status)) return true;
  return status >= 500 || status === 408 || status === 429;
};

// One automatic retry for a delete request: the backend can fail transiently
// when a follow-up delete arrives while the previous one is still processing.
const deleteWithRetry = async (
  attempt: () => Promise<unknown>,
  id: string
) => {
  try {
    await attempt();
  } catch (firstError) {
    if (!isRetryableError(firstError)) throw firstError;
    // eslint-disable-next-line no-console
    console.warn("delete failed, retrying once", { id, error: firstError });
    await wait(2500);
    await attempt();
  }
};

const getScopeKey = (scope: DocumentMoveSourceScope) =>
  scope.sectionKey === "id"
    ? `${scope.sectionKey}:${scope.sourceId ?? ""}`
    : `${scope.sectionKey}:${scope.sourceId ?? ""}:${scope.sourceIndex ?? ""}:${scope.slot ?? ""}`;

const documentsShareScope = (
  first: MovableDocument | undefined,
  next: MovableDocument
) =>
  !first ||
  getScopeKey(first.source) === getScopeKey(next.source);

const getDestinationQueueKey = (destination: DocumentMoveDestination) =>
  [
    destination.sectionKey,
    destination.kind,
    destination.sourceId ?? "",
    destination.idType ?? "",
    destination.idSlot ?? "",
  ].join(":");

type MoveJob = {
  batchId: number;
  document: MovableDocument;
  destination: DocumentMoveDestination;
  payload: Record<string, unknown>;
};

type MoveBatch = {
  id: number;
  destination: DocumentMoveDestination;
  sourceScopeKey: string | null;
  total: number;
  completed: number;
  successfulDocuments: MovableDocument[];
  failedMoves: Array<{ document: MovableDocument; error: any; payload: Record<string, unknown> }>;
  affectedSections: Set<DocumentMoveSectionKey | "trash">;
};

// Host forms rebuild their documents arrays on every render; comparing by
// content lets the registry skip state updates (and the re-renders of every
// context consumer they would cause) when nothing actually changed.
const movableDocumentListsEqual = (
  a: MovableDocument[] | undefined,
  b: MovableDocument[]
) =>
  !!a &&
  a.length === b.length &&
  a.every(
    (document, index) =>
      document.id === b[index].id &&
      document.label === b[index].label &&
      document.isOfferLetter === b[index].isOfferLetter &&
      getScopeKey(document.source) === getScopeKey(b[index].source)
  );

export const useDocumentMove = () => React.useContext(DocumentMoveContext);

type DocumentMoveProviderProps = {
  children: React.ReactNode;
  trnId?: string | number | null;
  bulkActionsEnabled?: boolean;
  isMoveDestinationVisible?: () => boolean;
  onRequestMoveFallback?: () => void;
  onMoveFallbackClose?: () => void;
};

export const DocumentMoveProvider = ({
  children,
  trnId,
  bulkActionsEnabled = true,
  isMoveDestinationVisible,
  onRequestMoveFallback,
  onMoveFallbackClose,
}: DocumentMoveProviderProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [documentsByScope, setDocumentsByScope] = React.useState<
    Record<string, MovableDocument[]>
  >({});
  const [selectedDocuments, setSelectedDocuments] = React.useState<
    MovableDocument[]
  >([]);
  const [isMoveMode, setIsMoveMode] = React.useState(false);
  const [, setLegacyIsMoving] = React.useState(false);
  const [queuedMoveJobCount, setQueuedMoveJobCount] = React.useState(0);
  const [pendingMoveIdsBySource, setPendingMoveIdsBySource] = React.useState<Record<string, string>>({});
  const [lockedDocumentIds, setLockedDocumentIds] = React.useState<Set<string>>(new Set());
  const [dragSelectedIds, setDragSelectedIds] = React.useState<string[]>([]);
  const [pendingDeleteDocuments, setPendingDeleteDocuments] = React.useState<
    MovableDocument[]
  >([]);
  // Drag-to-trash clears its drag state on dragend, so the dialog must delete
  // the snapshot it opened with. A checkbox selection, by contrast, stays live
  // — clearing it (or an in-flight move clearing it) should cancel the delete.
  const [pendingDeleteFromDrag, setPendingDeleteFromDrag] = React.useState(false);
  const [lastMessageState, setLastMessageState] = React.useState<{
    text: string;
    scopeKey: string | null;
  }>({ text: "", scopeKey: null });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [processingScopeKeys, setProcessingScopeKeys] = React.useState<Set<string>>(new Set());
  const [celebrationId, setCelebrationId] = React.useState("");
  const processingScopeKey = Array.from(processingScopeKeys)[0] ?? null;
  const isMoving = queuedMoveJobCount > 0;
  const moveInFlightRef = React.useRef(false);
  const deleteInFlightRef = React.useRef(false);
  const destinationQueuesRef = React.useRef(new Map<string, MoveJob[]>());
  const runningDestinationQueuesRef = React.useRef(new Set<string>());
  const destinationQueueHasSubmittedJobRef = React.useRef(new Set<string>());
  const batchesRef = React.useRef(new Map<number, MoveBatch>());
  const nextBatchIdRef = React.useRef(1);
  const processingScopeCountsRef = React.useRef(new Map<string, number>());
  const celebrationTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const moveFallbackCloseTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleRefreshTimersRef = React.useRef(new Set<ReturnType<typeof setTimeout>>());
  const pendingNotificationTimersRef = React.useRef(new Set<ReturnType<typeof setTimeout>>());
  const moveNotificationChainRef = React.useRef(Promise.resolve());
  const notificationVisibleUntilRef = React.useRef(0);
  const isMountedRef = React.useRef(true);

  const triggerCelebration = React.useCallback((id: string) => {
    if (celebrationTimerRef.current) clearTimeout(celebrationTimerRef.current);
    setCelebrationId(id);
    celebrationTimerRef.current = setTimeout(() => setCelebrationId(""), CELEBRATION_DURATION_MS);
  }, []);

  const scheduleMoveFallbackClose = React.useCallback(() => {
    if (!onMoveFallbackClose) return;
    if (moveFallbackCloseTimerRef.current) clearTimeout(moveFallbackCloseTimerRef.current);
    moveFallbackCloseTimerRef.current = setTimeout(() => {
      moveFallbackCloseTimerRef.current = null;
      onMoveFallbackClose();
    }, CELEBRATION_DURATION_MS);
  }, [onMoveFallbackClose]);

  React.useEffect(
    () => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
        if (celebrationTimerRef.current) clearTimeout(celebrationTimerRef.current);
        if (moveFallbackCloseTimerRef.current) clearTimeout(moveFallbackCloseTimerRef.current);
        settleRefreshTimersRef.current.forEach((timer) => clearTimeout(timer));
        settleRefreshTimersRef.current.clear();
        pendingNotificationTimersRef.current.forEach((timer) => clearTimeout(timer));
        pendingNotificationTimersRef.current.clear();
      };
    },
    []
  );

  const waitForMoveNotification = React.useCallback((ms: number) => {
    return new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        pendingNotificationTimersRef.current.delete(timer);
        resolve();
      }, ms);
      pendingNotificationTimersRef.current.add(timer);
    });
  }, []);

  // Show move-result toasts one at a time. The next notify() call waits until
  // the previous toast has been on screen for its display duration plus the
  // handoff gap; enqueueing sooner would make CustomNotification close it.
  const enqueueMoveResultNotifications = React.useCallback(
    (notifications: MoveResultNotification[]) => {
      notifications.forEach((notification) => {
        moveNotificationChainRef.current = moveNotificationChainRef.current
          .then(async () => {
            if (!isMountedRef.current) return;
            const waitMs = remainingNotificationHoldMs(
              notificationVisibleUntilRef.current,
              Date.now()
            );
            if (waitMs > 0) await waitForMoveNotification(waitMs);
            if (!isMountedRef.current) return;
            const shownAt = Date.now();
            notify(notification.message, {
              type: notification.type,
              autoHideDuration: notification.autoHideDuration,
              anchorOrigin: { vertical: "top", horizontal: "right" },
            });
            notificationVisibleUntilRef.current = nextNotificationVisibleUntil(
              shownAt,
              notification.autoHideDuration
            );
          })
          .catch(() => undefined);
      });
    },
    [notify, waitForMoveNotification]
  );

  // The request response only confirms that the move was accepted. The
  // backend can finish SOCR and update the destination after the first
  // refetch, so schedule a short sequence of targeted checks. Keeping timer
  // handles individually means quick, overlapping batches cannot cancel a
  // prior batch's destination refresh.
  const scheduleSettleRefresh = React.useCallback(
    (sections: Array<DocumentMoveSectionKey | "trash">, delayMs: number) => {
      MOVE_SETTLE_REFRESH_RETRY_OFFSETS_MS.forEach((offset) => {
        let timer: ReturnType<typeof setTimeout>;
        timer = setTimeout(() => {
          settleRefreshTimersRef.current.delete(timer);
          void invalidateDocumentQueriesForSections(queryClient, sections, { trnId });
        }, delayMs + offset);
        settleRefreshTimersRef.current.add(timer);
      });
    },
    [queryClient, trnId]
  );

  const selectedIds = React.useMemo(
    () => new Set(selectedDocuments.map((document) => document.id)),
    [selectedDocuments]
  );
  const pendingMoveIds = React.useMemo(
    () => new Set(Object.keys(pendingMoveIdsBySource)),
    [pendingMoveIdsBySource]
  );
  const dragDocuments = React.useMemo(() => {
    if (!dragSelectedIds.length) return [];
    const allDocuments = Object.values(documentsByScope).flat();
    return dragSelectedIds
      .filter((id) => !pendingMoveIds.has(id))
      .map((id) => allDocuments.find((document) => document.id === id))
      .filter((document): document is MovableDocument => Boolean(document));
  }, [documentsByScope, dragSelectedIds, pendingMoveIds]);
  // A drag in progress must win over a stale checkbox selection: FilePreview
  // allows dragging a document that isn't part of the current selection (it
  // sets dragSelectedIds to just that one document), and drop handlers read
  // activeDocuments while the drag is still active (dragend, which clears
  // dragSelectedIds, fires after drop). Falling back to selectedDocuments
  // here would move/delete the unrelated live selection instead of the
  // document the user actually dragged.
  const activeDocuments = React.useMemo(
    () => dragDocuments.length ? dragDocuments : selectedDocuments,
    [dragDocuments, selectedDocuments]
  );

  const setLastMessage = React.useCallback(
    (text: string, scopeKey: string | null = null) => {
      setLastMessageState({ text, scopeKey });
    },
    []
  );

  const registerDocuments = React.useCallback(
    (scopeKey: string, documents: MovableDocument[]) => {
      setDocumentsByScope((current) =>
        movableDocumentListsEqual(current[scopeKey], documents)
          ? current
          : { ...current, [scopeKey]: documents }
      );
    },
    []
  );

  React.useEffect(() => {
    setPendingMoveIdsBySource((current) => {
      let changed = false;
      const next = { ...current };
      Object.entries(current).forEach(([documentId, sourceScopeKey]) => {
        if (!Object.prototype.hasOwnProperty.call(documentsByScope, sourceScopeKey)) return;
        const sourceDocuments = documentsByScope[sourceScopeKey] || [];
        if (!sourceDocuments.some((document) => document.id === documentId)) {
          delete next[documentId];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [documentsByScope]);

  // A selected document can vanish from its registered list without going
  // through the move/delete flow here (e.g. the legacy per-file delete
  // button, or any other refetch that drops it) — reconcile the selection so
  // the strip badge, the confirm dialog, and what actually gets sent to the
  // backend never disagree.
  React.useEffect(() => {
    setSelectedDocuments((current) => {
      if (!current.length) return current;
      const next = current.filter((document) => {
        const scopeKey = getScopeKey(document.source);
        if (!Object.prototype.hasOwnProperty.call(documentsByScope, scopeKey)) return true;
        const sourceDocuments = documentsByScope[scopeKey] || [];
        return sourceDocuments.some((candidate) => candidate.id === document.id);
      });
      return next.length === current.length ? current : next;
    });
  }, [documentsByScope]);

  const unregisterDocuments = React.useCallback((scopeKey: string) => {
    setDocumentsByScope((current) => {
      const next = { ...current };
      delete next[scopeKey];
      return next;
    });
  }, []);

  const getDocumentsForScope = React.useCallback(
    (scope: DocumentMoveSourceScope) => {
      return documentsByScope[getScopeKey(scope)] || [];
    },
    [documentsByScope]
  );

  const clearSelection = React.useCallback((options?: { closeMoveFallback?: boolean }) => {
    setSelectedDocuments([]);
    setDragSelectedIds([]);
    setPendingDeleteDocuments([]);
    setIsMoveMode(false);
    setLastMessage("");
    if (options?.closeMoveFallback !== false) {
      if (moveFallbackCloseTimerRef.current) {
        clearTimeout(moveFallbackCloseTimerRef.current);
        moveFallbackCloseTimerRef.current = null;
      }
      onMoveFallbackClose?.();
    }
  }, [onMoveFallbackClose, setLastMessage]);

  React.useEffect(() => {
    if (bulkActionsEnabled) return;
    clearSelection();
  }, [bulkActionsEnabled, clearSelection]);

  const toggleDocumentSelection = React.useCallback((document: MovableDocument) => {
    if (!bulkActionsEnabled) return;
    if (pendingMoveIds.has(document.id)) return;
    if (lockedDocumentIds.has(document.id)) return;
    setSelectedDocuments((current) => {
      let next: MovableDocument[];
      if (current.some((selected) => selected.id === document.id)) {
        next = current.filter((selected) => selected.id !== document.id);
      } else if (!documentsShareScope(current[0], document)) {
        setLastMessage(
          "Selection is limited to one source or section.",
          getScopeKey(document.source)
        );
        next = [document];
      } else {
        next = [...current, document];
      }
      setIsMoveMode(next.length > 0);
      if (!next.length) onMoveFallbackClose?.();
      return next;
    });
  }, [bulkActionsEnabled, lockedDocumentIds, onMoveFallbackClose, pendingMoveIds, setLastMessage]);

  const setDocumentLocked = React.useCallback((documentId: string, locked: boolean) => {
    if (!documentId) return;
    setLockedDocumentIds((current) => {
      const next = new Set(current);
      if (locked) next.add(documentId);
      else next.delete(documentId);
      return next.size === current.size && Array.from(next).every((id) => current.has(id))
        ? current
        : next;
    });
    if (locked) {
      setSelectedDocuments((current) => {
        const next = current.filter((document) => document.id !== documentId);
        if (!next.length) setIsMoveMode(false);
        return next;
      });
      setDragSelectedIds((current) => current.filter((id) => id !== documentId));
      setPendingDeleteDocuments((current) => current.filter((document) => document.id !== documentId));
    }
  }, []);

  const selectDocuments = React.useCallback(
    (documents: MovableDocument[]) => {
      if (!bulkActionsEnabled) return;
      const realDocuments = documents.filter((document) =>
        isRealDocumentId(document.id) &&
        !pendingMoveIds.has(document.id) &&
        !lockedDocumentIds.has(document.id)
      );
      setSelectedDocuments(realDocuments);
      setIsMoveMode(realDocuments.length > 0);
      setLastMessage("");
      if (!realDocuments.length) onMoveFallbackClose?.();
    },
    [bulkActionsEnabled, lockedDocumentIds, onMoveFallbackClose, pendingMoveIds, setLastMessage]
  );

  const beginMoveMode = React.useCallback(() => {
    if (!bulkActionsEnabled) return;
    if (!selectedDocuments.length) {
      setLastMessage("Select one or more documents before moving.");
      return;
    }
    setIsMoveMode(true);
    const shouldUseFallback =
      Boolean(onRequestMoveFallback) &&
      (!isMoveDestinationVisible || !isMoveDestinationVisible());
    if (shouldUseFallback) onRequestMoveFallback?.();
    notify(
      shouldUseFallback
        ? "Choose a destination from the move drawer."
        : "Choose a destination in the sidebar outline.",
      {
        type: "info",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      }
    );
  }, [bulkActionsEnabled, isMoveDestinationVisible, notify, onRequestMoveFallback, selectedDocuments.length, setLastMessage]);

  const legacyMoveSelectionToDestination = React.useCallback(
    async (destination: DocumentMoveDestination) => {
      if (!bulkActionsEnabled) return;
      if (moveInFlightRef.current || deleteInFlightRef.current) {
        return;
      }

      const documentsToMove = activeDocuments;
      const selectionScopeKey = documentsToMove[0]
        ? getScopeKey(documentsToMove[0].source)
        : null;
      const validation = validateDocumentMove(documentsToMove, destination);
      if (!validation.valid) {
        const message = validation.message || "This destination cannot receive the selected documents.";
        setLastMessage(message, selectionScopeKey);
        notify(message, {
          type: "warning",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        return;
      }

      const documentId = getMovePayloadDocumentId(destination);
      const sourceId = getMovePayloadSourceId(destination);
      if (!trnId || documentId == null || sourceId == null) {
        const message =
          "Document move could not start because the selected destination is incomplete. Please choose the destination again.";
        setLastMessage(message, selectionScopeKey);
        notify(message, {
          type: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        return;
      }

      const needsProcessingDelays = destinationRequiresProcessing(destination);
      // Only the sections a move touches need refetching: the documents'
      // source section(s) and the destination section.
      const affectedSections = Array.from(
        new Set<DocumentMoveSectionKey | "trash">([
          ...documentsToMove.map((document) => document.source.sectionKey),
          destination.sectionKey,
        ])
      );

      moveInFlightRef.current = true;
      setLegacyIsMoving(true);
      setProcessingScopeKeys(new Set(selectionScopeKey ? [selectionScopeKey] : []));
      setPendingMoveIdsBySource((current) => {
        const next = { ...current };
        documentsToMove.forEach((document) => {
          next[document.id] = getScopeKey(document.source);
        });
        return next;
      });
      notify(`Moving ${documentsToMove.length} document${documentsToMove.length === 1 ? "" : "s"}...`, {
        type: "info",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });

      const successfulDocuments: MovableDocument[] = [];
      const failedMoves: Array<{ document: MovableDocument; error: any; payload: Record<string, unknown> }> = [];
      const submitMoveRequest = async (payload: Record<string, unknown>, document: MovableDocument) => {
        for (let attempt = 0; ; attempt += 1) {
          try {
            await dataProvider.create("automovedocument", { data: payload });
            return;
          } catch (error) {
            const retryDelay = MOVE_RETRY_DELAYS_MS[attempt];
            if (!isRetryableError(error) || retryDelay == null) throw error;
            // eslint-disable-next-line no-console
            console.warn("automovedocument failed, retrying", {
              applicationDocumentId: document.id,
              attempt: attempt + 1,
              error,
            });
            await wait(retryDelay);
          }
        }
      };

      try {
        for (let i = 0; i < documentsToMove.length; i += 1) {
          const document = documentsToMove[i];
          // Sections that process documents handle each move asynchronously
          // on the backend and can fail when a follow-up move arrives while
          // the previous one is still processing — space those out. A
          // non-processing destination (Other Documents) has no such race, so
          // its documents move back-to-back.
          if (i > 0 && needsProcessingDelays) await wait(MOVE_REQUEST_DELAY_MS);
          const payload = {
            trn_id: String(trnId).trim(),
            document_id: String(documentId).trim(),
            source_id: String(sourceId).trim(),
            // Employment Income rejects anything that isn't an original pay-stub
            // PDF (photos/screenshots/image PDFs included) unless the offer-letter
            // path is allowed. A move is an explicit relocate of an already
            // uploaded document, so permit it the same way the manual offer-letter
            // upload does — otherwise e.g. an image PDF from Other Documents fails
            // to move into a paystub source.
            allowOfferLetter: destination.sectionKey === "paystub",
            application_document_id: String(document.id).trim(),
          };
          try {
            await submitMoveRequest(payload, document);
            successfulDocuments.push(document);
          } catch (error) {
            failedMoves.push({ document, error, payload });
            // eslint-disable-next-line no-console
            console.error("automovedocument failed for document", {
              destinationId: destination.id,
              sectionKey: destination.sectionKey,
              sentSourceId: payload.source_id,
              sentDocumentId: payload.document_id,
              sentTrnId: payload.trn_id,
              sentApplicationDocumentId: payload.application_document_id,
              serverMessage: (error as any)?.message,
              payload,
              destination,
              error,
            });
          }
        }

        setPendingMoveIdsBySource((current) => {
          if (!failedMoves.length) return current;
          const next = { ...current };
          failedMoves.forEach(({ document }) => delete next[document.id]);
          return next;
        });

        // automovedocument relocates the documents, but the ID section renders
        // from an identity record that still references them — clear those
        // references or the files keep showing in both sections.
        if (
          successfulDocuments.length > 0 &&
          successfulDocuments[0]?.source.sectionKey === "id" &&
          destination.sectionKey !== "id"
        ) {
          try {
            const { data: identityRecord } = await dataProvider.getOne(
              "proofOfIdsData",
              { id: trnId }
            );
            const cleanupPayload = buildIdentityCleanupPayload(
              identityRecord?.identification_data,
              successfulDocuments,
              trnId
            );
            if (cleanupPayload) {
              await dataProvider.update("applicant", {
                id: trnId,
                data: cleanupPayload,
                previousData: identityRecord,
              });
            }
          } catch (cleanupError) {
            // eslint-disable-next-line no-console
            console.error("proof of id cleanup after move failed", cleanupError);
            notify(
              "Documents moved, but the ID section could not be updated automatically. Please verify the ID section.",
              {
                type: "warning",
                anchorOrigin: { vertical: "top", horizontal: "right" },
              }
            );
          }
        }

        const resultNotifications = buildMoveResultNotificationSequence({
          succeeded: successfulDocuments.length,
          failed: failedMoves.length,
          failureReasons: failedMoves.map((failedMove) =>
            typeof failedMove.error?.message === "string" ? failedMove.error.message : ""
          ),
        });
        if (resultNotifications.persistentMessage) {
          setLastMessage(resultNotifications.persistentMessage, selectionScopeKey);
        }
        enqueueMoveResultNotifications(resultNotifications.notifications);
        if (failedMoves.length) {
          clearSelection();
        } else {
          triggerCelebration(destination.kind === "id-type" ? "proof-of-id" : destination.id);
          clearSelection({ closeMoveFallback: false });
          scheduleMoveFallbackClose();
        }
      } finally {
        // Unlock before refetching: invalidateQueries only resolves after the
        // active queries finish refetching, and the pending-move guard (not
        // the section-wide isMoving lock) is what actually prevents a second
        // operation on the moved documents. Holding the lock through the
        // refetch is what made every move feel slow.
        moveInFlightRef.current = false;
        setLegacyIsMoving(false);
        setProcessingScopeKeys(new Set());
        // Successful moves stay pending: the backend processes them
        // asynchronously after the request is accepted, so clearing this now —
        // would let the document be re-selected and moved/deleted again while
        // the first move is still in flight. The prune effect (keyed on
        // documentsByScope) clears it once the document actually leaves its
        // source scope's list, whether that happens from this refresh or the
        // settle refresh. Failed moves were already cleared above so they can
        // be retried immediately.
        scheduleSettleRefresh(
          affectedSections,
          needsProcessingDelays
            ? MOVE_SETTLE_REFRESH_DELAY_MS
            : MOVE_SETTLE_REFRESH_FAST_DELAY_MS
        );
        await invalidateDocumentQueriesForSections(queryClient, affectedSections, { trnId });
      }
    },
    [activeDocuments, bulkActionsEnabled, clearSelection, dataProvider, enqueueMoveResultNotifications, notify, queryClient, scheduleMoveFallbackClose, scheduleSettleRefresh, setLastMessage, triggerCelebration, trnId]
  );

  const moveSelectionToDestination = React.useCallback(
    async (destination: DocumentMoveDestination) => {
      if (!bulkActionsEnabled || deleteInFlightRef.current) return;

      const documentsToMove = [...activeDocuments];
      const selectionScopeKey = documentsToMove[0]
        ? getScopeKey(documentsToMove[0].source)
        : null;
      const validation = validateDocumentMove(documentsToMove, destination);
      if (!validation.valid) {
        const message = validation.message || "This destination cannot receive the selected documents.";
        setLastMessage(message, selectionScopeKey);
        notify(message, {
          type: "warning",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        return;
      }

      const documentId = getMovePayloadDocumentId(destination);
      const sourceId = getMovePayloadSourceId(destination);
      if (!trnId || documentId == null || sourceId == null) {
        const message = "Document move could not start because the selected destination is incomplete. Please choose the destination again.";
        setLastMessage(message, selectionScopeKey);
        notify(message, {
          type: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        return;
      }

      const batchId = nextBatchIdRef.current++;
      const batch: MoveBatch = {
        id: batchId,
        destination,
        sourceScopeKey: selectionScopeKey,
        total: documentsToMove.length,
        completed: 0,
        successfulDocuments: [],
        failedMoves: [],
        affectedSections: new Set([
          ...documentsToMove.map((document) => document.source.sectionKey),
          destination.sectionKey,
        ]),
      };
      batchesRef.current.set(batchId, batch);

      const queueKey = getDestinationQueueKey(destination);
      const queue = destinationQueuesRef.current.get(queueKey) || [];
      documentsToMove.forEach((document) => {
        queue.push({
          batchId,
          document,
          destination,
          payload: {
            trn_id: String(trnId).trim(),
            document_id: String(documentId).trim(),
            source_id: String(sourceId).trim(),
            allowOfferLetter: destination.sectionKey === "paystub",
            application_document_id: String(document.id).trim(),
          },
        });
      });
      destinationQueuesRef.current.set(queueKey, queue);
      setQueuedMoveJobCount((current) => current + documentsToMove.length);
      setPendingMoveIdsBySource((current) => {
        const next = { ...current };
        documentsToMove.forEach((document) => {
          next[document.id] = getScopeKey(document.source);
        });
        return next;
      });
      clearSelection({ closeMoveFallback: false });
      notify(`Queued ${documentsToMove.length} document${documentsToMove.length === 1 ? "" : "s"} to move.`, {
        type: "info",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });

      const updateProcessingScope = (scopeKey: string, active: boolean) => {
        const counts = processingScopeCountsRef.current;
        const nextCount = (counts.get(scopeKey) || 0) + (active ? 1 : -1);
        if (nextCount > 0) counts.set(scopeKey, nextCount);
        else counts.delete(scopeKey);
        setProcessingScopeKeys(new Set(counts.keys()));
      };

      const submitMoveRequest = async (job: MoveJob) => {
        for (let attempt = 0; ; attempt += 1) {
          try {
            await dataProvider.create("automovedocument", { data: job.payload });
            return;
          } catch (error) {
            const retryDelay = MOVE_RETRY_DELAYS_MS[attempt];
            if (!isRetryableError(error) || retryDelay == null) throw error;
            // eslint-disable-next-line no-console
            console.warn("automovedocument failed, retrying", {
              applicationDocumentId: job.document.id,
              attempt: attempt + 1,
              error,
            });
            await wait(retryDelay);
          }
        }
      };

      const finishBatch = async (completedBatch: MoveBatch) => {
        if (
          completedBatch.successfulDocuments.length > 0 &&
          completedBatch.successfulDocuments[0]?.source.sectionKey === "id" &&
          completedBatch.destination.sectionKey !== "id"
        ) {
          try {
            const { data: identityRecord } = await dataProvider.getOne("proofOfIdsData", { id: trnId });
            const cleanupPayload = buildIdentityCleanupPayload(
              identityRecord?.identification_data,
              completedBatch.successfulDocuments,
              trnId
            );
            if (cleanupPayload) {
              await dataProvider.update("applicant", {
                id: trnId,
                data: cleanupPayload,
                previousData: identityRecord,
              });
            }
          } catch (cleanupError) {
            // eslint-disable-next-line no-console
            console.error("proof of id cleanup after move failed", cleanupError);
            notify("Documents moved, but the ID section could not be updated automatically. Please verify the ID section.", {
              type: "warning",
              anchorOrigin: { vertical: "top", horizontal: "right" },
            });
          }
        }

        const resultNotifications = buildMoveResultNotificationSequence({
          succeeded: completedBatch.successfulDocuments.length,
          failed: completedBatch.failedMoves.length,
          failureReasons: completedBatch.failedMoves.map((failedMove) =>
            typeof failedMove.error?.message === "string" ? failedMove.error.message : ""
          ),
        });
        if (resultNotifications.persistentMessage) {
          setLastMessage(resultNotifications.persistentMessage, completedBatch.sourceScopeKey);
        }
        enqueueMoveResultNotifications(resultNotifications.notifications);
        const succeeded = completedBatch.successfulDocuments.length;
        if (succeeded) {
          triggerCelebration(
            completedBatch.destination.kind === "id-type"
              ? "proof-of-id"
              : completedBatch.destination.id
          );
          scheduleMoveFallbackClose();
        }
        const affectedSections = Array.from(completedBatch.affectedSections);
        scheduleSettleRefresh(
          affectedSections,
          destinationRequiresProcessing(completedBatch.destination)
            ? MOVE_SETTLE_REFRESH_DELAY_MS
            : MOVE_SETTLE_REFRESH_FAST_DELAY_MS
        );
        void invalidateDocumentQueriesForSections(queryClient, affectedSections, { trnId });
        batchesRef.current.delete(completedBatch.id);
      };

      const runQueue = async () => {
        if (runningDestinationQueuesRef.current.has(queueKey)) return;
        runningDestinationQueuesRef.current.add(queueKey);
        try {
          let job: MoveJob | undefined;
          while ((job = destinationQueuesRef.current.get(queueKey)?.shift())) {
            if (
              destinationQueueHasSubmittedJobRef.current.has(queueKey) &&
              destinationRequiresProcessing(job.destination)
            ) {
              await wait(MOVE_REQUEST_DELAY_MS);
            }
            const scopeKey = getScopeKey(job.document.source);
            updateProcessingScope(scopeKey, true);
            let error: any = null;
            try {
              await submitMoveRequest(job);
              destinationQueueHasSubmittedJobRef.current.add(queueKey);
            } catch (caughtError) {
              error = caughtError;
              const failedDocumentId = job.document.id;
              // eslint-disable-next-line no-console
              console.error("automovedocument failed for document", {
                destinationId: job.destination.id,
                sectionKey: job.destination.sectionKey,
                payload: job.payload,
                error: caughtError,
              });
              setPendingMoveIdsBySource((current) => {
                const next = { ...current };
                delete next[failedDocumentId];
                return next;
              });
            } finally {
              updateProcessingScope(scopeKey, false);
            }

            const completedBatch = batchesRef.current.get(job.batchId);
            if (!completedBatch) continue;
            completedBatch.completed += 1;
            if (error) completedBatch.failedMoves.push({ document: job.document, error, payload: job.payload });
            else completedBatch.successfulDocuments.push(job.document);
            setQueuedMoveJobCount((current) => Math.max(0, current - 1));
            if (completedBatch.completed === completedBatch.total) {
              await finishBatch(completedBatch);
            }
          }
        } finally {
          runningDestinationQueuesRef.current.delete(queueKey);
          if (destinationQueuesRef.current.get(queueKey)?.length) void runQueue();
        }
      };

      void runQueue();
    },
    [activeDocuments, bulkActionsEnabled, clearSelection, dataProvider, enqueueMoveResultNotifications, notify, queryClient, scheduleMoveFallbackClose, scheduleSettleRefresh, setLastMessage, triggerCelebration, trnId]
  );

  const performDelete = React.useCallback(async (documents: MovableDocument[]) => {
    if (!bulkActionsEnabled) return;
    // Synchronous ref guard: isDeleting is render-state and lags a tick, so a
    // double-click on Confirm before re-render could otherwise start two
    // overlapping delete loops (mirrors moveInFlightRef on the move path).
    if (moveInFlightRef.current || deleteInFlightRef.current) return;
    // Snapshot immediately: a drag-to-trash delete hands the dragged documents
    // straight in (its drag state is cleared on dragend), and a selection delete
    // must not be cancelled by an in-flight move clearing the live selection.
    const toDelete = [...documents];
    if (!toDelete.length) {
      setConfirmDeleteOpen(false);
      return;
    }
    const isIdSelection = toDelete[0]?.source.sectionKey === "id";
    const deleteScopeKey = toDelete[0] ? getScopeKey(toDelete[0].source) : null;
    deleteInFlightRef.current = true;
    setIsDeleting(true);
    setProcessingScopeKeys((current) => {
      const next = new Set(current);
      if (deleteScopeKey) next.add(deleteScopeKey);
      return next;
    });

    const deletedDocuments: MovableDocument[] = [];
    const failedDeletes: Array<{ document: MovableDocument; error: any }> = [];

    try {
      // Every document — ID front/back included — soft-deletes through
      // deletedocument, the same endpoint the per-file delete button uses in
      // production. It removes only the document it is given (landing it in
      // the Deleted Documents list) and leaves the other slot alone, so
      // deleting the front does not take the back with it. deleteiddocument
      // is deliberately avoided here: it clears the whole identity record and
      // orphans the slot the user is keeping.
      for (let i = 0; i < toDelete.length; i += 1) {
        // Space out requests for the same reason moves do: the backend can
        // fail when a follow-up arrives while the previous one is processing.
        if (i > 0) await wait(1500);
        try {
          await deleteWithRetry(
            () => dataProvider.deleteDocument(toDelete[i].id),
            toDelete[i].id
          );
          deletedDocuments.push(toDelete[i]);
        } catch (error) {
          failedDeletes.push({ document: toDelete[i], error });
          // eslint-disable-next-line no-console
          console.error("deleteDocument failed for document", {
            id: toDelete[i].id,
            error,
          });
        }
      }

      // The ID section's slots are tracked by the identity record, not just
      // the document's deleted flag. Soft-deleting the front leaves its
      // reference behind, so the slot still reads as occupied and a restore
      // is rejected with "already present in that section". Clear only the
      // deleted slots' references (preserving the others) — the same cleanup
      // the move path runs — so the slot is genuinely open and the document
      // can be restored. Run this for whatever actually got deleted, even if
      // a later document in the batch failed — a thrown error must not skip
      // cleanup for documents that were already removed.
      if (isIdSelection && deletedDocuments.length) {
        try {
          const { data: identityRecord } = await dataProvider.getOne(
            "proofOfIdsData",
            { id: trnId }
          );
          const cleanupPayload = buildIdentityCleanupPayload(
            identityRecord?.identification_data,
            deletedDocuments,
            trnId as string | number
          );
          if (cleanupPayload) {
            await dataProvider.update("applicant", {
              id: trnId,
              data: cleanupPayload,
              previousData: identityRecord,
            });
          }
        } catch (cleanupError) {
          // eslint-disable-next-line no-console
          console.error(
            "identity record cleanup after ID delete failed",
            cleanupError
          );
        }
      }

      if (failedDeletes.length) {
        const reason = toErrorMessage(failedDeletes[0].error, "");
        let message: string;
        if (deletedDocuments.length) {
          const summary = `Deleted ${deletedDocuments.length} of ${toDelete.length} documents. ${failedDeletes.length} document${failedDeletes.length === 1 ? "" : "s"} failed to delete`;
          message = reason ? `${summary}: ${reason}` : `${summary} — please refresh and try again.`;
        } else {
          message = reason || "We couldn't delete the selected documents right now. Some may have already been deleted — please refresh and try again.";
        }
        clearSelection();
        setLastMessage(message, deleteScopeKey);
        notify(message, {
          type: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      } else {
        notify(`Deleted ${toDelete.length} document${toDelete.length === 1 ? "" : "s"}. Use Deleted documents to restore if needed.`, {
          type: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        triggerCelebration("trash");
        clearSelection();
      }
    } finally {
      // Deletes only touch the documents' source section(s) and the Deleted
      // Documents badge/list — refetch just those, scoped to this applicant.
      // Kept awaited (unlike the move path): deletes have no pending-id guard,
      // so the section stays locked until the refetched lists no longer offer
      // the deleted documents for re-selection.
      await invalidateDocumentQueriesForSections(
        queryClient,
        Array.from(new Set(toDelete.map((document) => document.source.sectionKey))),
        { trnId, extraQueryKeys: ["deletedDocument"] }
      );
      deleteInFlightRef.current = false;
      setIsDeleting(false);
      setConfirmDeleteOpen(false);
      setProcessingScopeKeys((current) => {
        const next = new Set(current);
        if (deleteScopeKey) next.delete(deleteScopeKey);
        return next;
      });
    }
  }, [bulkActionsEnabled, clearSelection, dataProvider, notify, queryClient, setLastMessage, triggerCelebration, trnId]);

  // The confirmation dialog deletes whatever the dialog was opened for. Drag
  // deletes don't go through here — they delete immediately (see
  // requestDeleteSelection).
  const confirmDeleteSelection = React.useCallback(
    () => performDelete(pendingDeleteFromDrag ? pendingDeleteDocuments : selectedDocuments),
    [pendingDeleteFromDrag, pendingDeleteDocuments, selectedDocuments, performDelete]
  );

  const requestDeleteSelection = React.useCallback((options?: { fromDrag?: boolean }) => {
    if (!bulkActionsEnabled) return;
    if (moveInFlightRef.current || deleteInFlightRef.current) {
      setLastMessage(
        "Documents are still being processed. Try again in a moment.",
        selectedDocuments[0] ? getScopeKey(selectedDocuments[0].source) : null
      );
      return;
    }
    if (!activeDocuments.length) {
      setLastMessage("Select one or more documents before deleting.");
      return;
    }
    // Dragging documents onto the trash deletes them immediately — no
    // confirmation dialog. activeDocuments resolves to the dragged documents
    // here (there is no selection), and performDelete snapshots them before any
    // await, so they survive dragend clearing the drag state.
    if (options?.fromDrag) {
      performDelete(activeDocuments);
      return;
    }
    // Selection delete (trash button click) still confirms. Snapshot what we're
    // deleting so the dialog shows the right count.
    setPendingDeleteFromDrag(!selectedDocuments.length);
    setPendingDeleteDocuments(activeDocuments);
    setConfirmDeleteOpen(true);
  }, [activeDocuments, bulkActionsEnabled, performDelete, selectedDocuments, setLastMessage]);

  const value = React.useMemo(
    () => ({
      bulkActionsEnabled,
      selectedDocuments,
      activeDocuments,
      selectedIds,
      pendingMoveIds,
      lockedDocumentIds,
      isMoveMode: isMoveMode || dragDocuments.length > 0,
      isMoving,
      isDeleting,
      processingScopeKey,
      processingScopeKeys,
      dragSelectedIds,
      lastMessage: lastMessageState.text,
      lastMessageScopeKey: lastMessageState.scopeKey,
      registerDocuments,
      unregisterDocuments,
      getDocumentsForScope,
      toggleDocumentSelection,
      setDocumentLocked,
      selectDocuments,
      clearSelection,
      beginMoveMode,
      setDragSelectedIds: bulkActionsEnabled ? setDragSelectedIds : () => undefined,
      moveSelectionToDestination,
      requestDeleteSelection,
      celebrationId,
    }),
    [
      beginMoveMode,
      bulkActionsEnabled,
      celebrationId,
      clearSelection,
      dragSelectedIds,
      activeDocuments,
      dragDocuments.length,
      getDocumentsForScope,
      isMoveMode,
      isMoving,
      isDeleting,
      lastMessageState,
      pendingMoveIds,
      lockedDocumentIds,
      moveSelectionToDestination,
      processingScopeKey,
      processingScopeKeys,
      registerDocuments,
      requestDeleteSelection,
      selectDocuments,
      selectedDocuments,
      selectedIds,
      toggleDocumentSelection,
      setDocumentLocked,
      unregisterDocuments,
    ]
  );

  // Mirror what confirm will actually delete: the snapshot for a drag delete,
  // the live selection for a checkbox delete (so the count tracks clears).
  const liveDeleteCount = (
    pendingDeleteFromDrag ? pendingDeleteDocuments : selectedDocuments
  ).length;
  // Confirming clears the selection before the dialog finishes animating
  // closed, which would briefly flash "0 selected documents". Hold the last
  // positive count so the dialog keeps showing the real number as it closes.
  const deleteDialogCountRef = React.useRef(0);
  if (confirmDeleteOpen && liveDeleteCount > 0) {
    deleteDialogCountRef.current = liveDeleteCount;
  }
  const deleteDialogCount = deleteDialogCountRef.current;

  return (
    <DocumentMoveContext.Provider value={value}>
      {children}
      <Confirm
        isOpen={confirmDeleteOpen}
        loading={isDeleting}
        title="Delete Documents"
        content={`Are you sure you want to delete ${deleteDialogCount} selected document${deleteDialogCount === 1 ? "" : "s"}?`}
        onConfirm={confirmDeleteSelection}
        onClose={() => setConfirmDeleteOpen(false)}
      />
    </DocumentMoveContext.Provider>
  );
};

export const DocumentSourceActionStrip = ({
  scope,
  documents,
  hasDropZone = true,
}: {
  scope: DocumentMoveSourceScope;
  documents: MovableDocument[];
  hasDropZone?: boolean;
}) => {
  const {
    selectedIds,
    pendingMoveIds,
    lockedDocumentIds,
    isMoving,
    isDeleting,
    processingScopeKeys,
    registerDocuments,
    unregisterDocuments,
    selectDocuments,
    beginMoveMode,
    requestDeleteSelection,
    bulkActionsEnabled,
  } = useDocumentMove();
  const scopeKey = getScopeKey(scope);

  // Grid sections render the strip between the upload control and thumbnail
  // grid. ID and SSN keep the compact fixed-slot layout, with a fixed-width
  // Select All toggle defined in CSS so its label never causes a resize.
  const isStackedLayout = scope.sectionKey !== "id" && scope.sectionKey !== "ssn";

  // Manual/unsaved rows share the files arrays with real documents but carry
  // placeholder ids; they must not be selectable or counted.
  const movableDocuments = documents.filter((document) =>
    isRealDocumentId(document.id)
  );
  const selectableDocuments = movableDocuments.filter(
    (document) => !pendingMoveIds.has(document.id) && !lockedDocumentIds.has(document.id)
  );

  // Register on content changes (registerDocuments bails out when the list is
  // shallow-equal); unregister only when the scope unmounts or changes, so a
  // parent re-render doesn't churn the registry with delete + re-add.
  React.useEffect(() => {
    if (!bulkActionsEnabled) return;
    registerDocuments(scopeKey, movableDocuments);
  });
  React.useEffect(
    () => {
      if (!bulkActionsEnabled) return undefined;
      return () => unregisterDocuments(scopeKey);
    },
    [bulkActionsEnabled, scopeKey, unregisterDocuments]
  );

  const selectedInScope = movableDocuments.filter((document) => selectedIds.has(document.id));
  const hasDocuments = movableDocuments.length > 0;
  const hasSelectableDocuments = selectableDocuments.length > 0;
  const selectedCount = selectedInScope.length;
  const hasSelection = selectedCount > 0;
  const allSelected = hasSelectableDocuments && selectedCount === selectableDocuments.length;
  const isProcessingScope =
    (isMoving || isDeleting) && processingScopeKeys.has(scopeKey);

  if (!bulkActionsEnabled) return null;
  if (!hasDocuments) return null;

  return (
    <div className={`document-action-strip print-hide${isStackedLayout ? " document-action-strip--stacked" : ""}${hasDropZone ? "" : " document-action-strip--no-dropzone"}${isProcessingScope ? " document-action-strip--processing" : ""}`} data-testid="document-action-strip">
      <button
        type="button"
        className={`document-action-select-all ${allSelected ? "selected" : ""}`}
        disabled={!hasSelectableDocuments || isDeleting}
        onClick={(event) => {
          selectDocuments(allSelected ? [] : selectableDocuments);
          // Blur so a following spacebar opens Quick Look instead of
          // re-triggering Select All.
          event.currentTarget.blur();
        }}
        aria-pressed={allSelected}
        aria-label={allSelected ? "Clear Selection" : "Select All"}
      >
        <FormatListBulletedIcon fontSize="small" aria-hidden="true" />
        <span>{allSelected ? "Clear Selection" : "Select All"}</span>
      </button>
      <div className="document-action-row">
        <button
          type="button"
          className="document-action-icon danger"
          disabled={!hasSelection || isDeleting}
          onClick={() => requestDeleteSelection()}
          onDragOver={(event) => {
            if (!event.dataTransfer.types.includes(DOCUMENT_DRAG_MIME)) return;
            event.preventDefault();
          }}
          onDrop={(event) => {
            if (!event.dataTransfer.types.includes(DOCUMENT_DRAG_MIME)) return;
            event.preventDefault();
            requestDeleteSelection({ fromDrag: true });
          }}
          title="Delete selected documents (drag documents here to delete)"
          aria-label={`Delete ${selectedCount} selected document${selectedCount === 1 ? "" : "s"}`}
        >
          <DeleteIcon fontSize="small" />
          <span className="document-action-count">{selectedCount}</span>
        </button>
        <button
          type="button"
          className="document-action-icon move"
          disabled={!hasSelection || isDeleting}
          onClick={beginMoveMode}
          title="Move selected documents"
          aria-label={`Move ${selectedCount} selected document${selectedCount === 1 ? "" : "s"}`}
        >
          <DriveFileMoveIcon fontSize="small" />
          <span className="document-action-count">{selectedCount}</span>
        </button>
      </div>
    </div>
  );
};
